import React, { useState, useEffect, useMemo, useCallback, lazy, Suspense } from 'react'; 
import './App.css';  
import Header from './components/Header';
import Hero from './components/Hero';
import PerformanceDashboard from './components/PerformanceDashboard'; 
import perfMonitor from './utils/performance';      
import { cachedFetch } from './utils/cache';      
import apiUsageTracker, { trackApiCall } from './utils/apiUsageTracker';  

// Lazy load components that are below the fold with better chunk names 
const CryptoGrid = lazy(() =>   
  import(/* webpackChunkName:   "crypto-grid" */ './components/CryptoGrid')   
);
const About = lazy(() =>  
  import(/* webpackChunkName: "about" */ './components/About') 
); 
const Contact = lazy(() =>   
  import(/* webpackChunkName: "contact" */ './components/Contact')
);
const Footer = lazy(() =>  
  import(/* webpackChunkName: "footer" */ './components/Footer')
);
const Auth = lazy(() =>
  import(/* webpackChunkName: "auth" */ './components/Auth')
);
  
// Loading component for lazy-loaded components 
const ComponentLoader = () => (
  <div style={{ 
    display: 'flex', 
    justifyContent: 'center', 
    alignItems: 'center', 
    minHeight: '200px',
    color: '#ffffff'
  }}>
    Loading...
  </div>
);

function App() {
  const [currentView, setCurrentView] = useState('auth'); // 'home', 'auth'
  const [cryptoData, setCryptoData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMoreData, setHasMoreData] = useState(true);

  // Handle navigation and authentication check
  useEffect(() => {
    const handleNavigation = () => {
      const path = window.location.pathname;
      const isAuthenticated = localStorage.getItem('isAuthenticated') === 'true';
      
      if (isAuthenticated && path === '/') {
        setCurrentView('home');
      } else if (path === '/login' || path === '/register' || !isAuthenticated) {
        setCurrentView('auth');
      } else {
        setCurrentView('home');
      }
    };

    handleNavigation();
    window.addEventListener('popstate', handleNavigation);
    return () => window.removeEventListener('popstate', handleNavigation);
  }, []);

  // Optimized fetch function with performance tracking, caching, and debouncing
  const fetchCryptoData = useCallback(async (page = 1, isLoadMore = false) => {
    return await perfMonitor.measureApiCall('coingecko_markets', async () => {
      try {
        if (!isLoadMore) {
          setLoading(true);
        } else {
          setLoadingMore(true);
        }
        
        // Using CoinGecko API with reduced data for better performance
        const COINGECKO_URL = `https://api.coingecko.com/api/v3/coins/markets?vs_currency=usd&order=market_cap_desc&per_page=20&page=${page}&sparkline=false&price_change_percentage=24h`;
        
        // Track API usage and use cached fetch with 30 second TTL
        const data = await trackApiCall(async () => {
          return await cachedFetch(COINGECKO_URL, {
            headers: {
              'Cache-Control': 'max-age=30'
            }
          }, 30000);
        });
        
        // Optimized data transformation with early return
        if (!data || data.length === 0) {
          setHasMoreData(false);
          return;
        }
        
        const transformedData = data.map((coin, index) => ({
          id: coin.id,
          name: coin.name,
          symbol: coin.symbol.toUpperCase(),
          cmc_rank: coin.market_cap_rank || ((page - 1) * 20 + index + 1),
          image: coin.image,
          quote: {
            USD: {
              price: coin.current_price,
              percent_change_24h: coin.price_change_percentage_24h || 0,
              market_cap: coin.market_cap,
              volume_24h: coin.total_volume
            }
          }
        }));
        
        if (isLoadMore) {
          setCryptoData(prevData => [...prevData, ...transformedData]);
        } else {
          setCryptoData(transformedData);
        }
        
        setHasMoreData(data.length === 20);
        setError(null);
      } catch (err) {
        setError(err.message);
        console.error('Error fetching crypto data:', err);
        throw err;
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    });
  }, []);

  // Optimized load more function
  const loadMoreCryptos = useCallback(() => {
    const nextPage = currentPage + 1;
    setCurrentPage(nextPage);
    fetchCryptoData(nextPage, true);
  }, [currentPage, fetchCryptoData]);

  // Debounced refresh function for better performance
  const debouncedRefresh = useMemo(
    () => perfMonitor.debounce(() => {
      setCurrentPage(1);
      fetchCryptoData(1, false);
    }, 500),
    [fetchCryptoData]
  );

  // Optimized refresh function
  const handleRefresh = useCallback(() => {
    debouncedRefresh();
  }, [debouncedRefresh]);

  // Memoized data to prevent unnecessary re-renders
  const memoizedCryptoData = useMemo(() => cryptoData, [cryptoData]);

  useEffect(() => {
    fetchCryptoData();
    
    // Log performance summary after initial load
    const timer = setTimeout(() => {
      perfMonitor.logReport();
      
      // Log API usage report
      console.log(apiUsageTracker.getFormattedReport());
    }, 3000);
    
    // Cleanup function to prevent memory leaks
    return () => {
      clearTimeout(timer);
      // Clear any pending debounced calls
      if (debouncedRefresh.cancel) {
        debouncedRefresh.cancel();
      }
    };
  }, [fetchCryptoData, debouncedRefresh]);

  return (
    <div className="App">
      <PerformanceDashboard />
      {currentView !== 'auth' && <Header />}
      <Hero cryptoData={memoizedCryptoData} />
      
      {currentView === 'auth' && (
        <Suspense fallback={<ComponentLoader />}>
          <Auth />
        </Suspense>
      )}
      
      <Suspense fallback={<ComponentLoader />}>
        <CryptoGrid 
          cryptoData={memoizedCryptoData} 
          loading={loading} 
          loadingMore={loadingMore}
          error={error} 
          hasMoreData={hasMoreData}
          onRefresh={handleRefresh}
          onLoadMore={loadMoreCryptos}
        />
      </Suspense>
      
      <Suspense fallback={<ComponentLoader />}>
        <About cryptoData={memoizedCryptoData} />
      </Suspense>
      
      <Suspense fallback={<ComponentLoader />}>
        <Contact />
      </Suspense>
      
      <Suspense fallback={<ComponentLoader />}>
        <Footer />
      </Suspense>
    </div>
  );
}

export default App;
